import { httpsCallable } from "firebase/functions";
import { getAuth } from "firebase/auth";
import * as Sentry from "@sentry/react";

/**
 * Drop-in replacement for httpsCallable that adds Sentry distributed tracing.
 * Passes trace context via the data payload so the backend withSentry wrapper
 * can continue the trace.
 *
 * Usage:
 *   // Before: const myFunc = httpsCallable(functions, "myFunc");
 *   // After:  const myFunc = tracedCallable(functions, "myFunc");
 *   const result = await myFunc({ orgId: "abc" });
 *
 * Accepts the same options as httpsCallable (e.g. { timeout: 20000 }).
 */
// Trace context travels in the DATA payload, not in headers: a callable's
// CORS allow-list does not include custom request headers, so the backend
// `withSentry` wrapper reads these two fields off `request.data`.
function traceFields(span) {
  return span
    ? {
        _sentryTrace: Sentry.spanToTraceHeader(span),
        _sentryBaggage: Sentry.spanToBaggageHeader(span),
      }
    : {};
}

export function tracedCallable(functions, name, options) {
  const callable = httpsCallable(functions, name, options);

  return (data) =>
    Sentry.startSpan(
      { name: `callable/${name}`, op: "http.client" },
      async (span) => callable({ ...data, ...traceFields(span) }),
    );
}

/**
 * The URL a callable is served from. `_url` is the SDK's own builder and the
 * only thing that knows whether `connectFunctionsEmulator` was called, so it
 * is preferred; the public fields are the fallback if it ever disappears.
 */
function callableUrl(functions, name) {
  if (typeof functions._url === "function") return functions._url(name);
  if (functions.customDomain) return `${functions.customDomain}/${name}`;
  const projectId = functions.app?.options?.projectId;
  return `https://${functions.region}-${projectId}.cloudfunctions.net/${name}`;
}

/**
 * `tracedCallable` for a call that must be cancellable.
 *
 * `httpsCallable` accepts no `AbortSignal` — in @firebase/functions 0.13.1 the
 * option exists only on `.stream()` — and when its underlying fetch rejects,
 * `postJSON` discards the reason and reports a bare `functions/internal`
 * (GH#416). A caller therefore cannot tell "I tore this down myself" from "the
 * backend is broken", and the reconciliation tool spent that ambiguity on a
 * user-facing warning it should never have shown.
 *
 * This speaks the same callable protocol over `fetch`, which does take a
 * signal: an aborted call rejects with a DOMException named `AbortError`,
 * distinguishable from every server-side outcome.
 *
 * @param {import("firebase/functions").Functions} functions
 * @param {string} name - callable name
 * @param {{signal?: AbortSignal}} [options]
 * @return {(data: object) => Promise<{data: any}>} resolves `{data}`, matching
 *   the shape `httpsCallable` returns, so call sites need no other change.
 */
export function abortableCallable(functions, name, { signal } = {}) {
  return (data) =>
    Sentry.startSpan(
      { name: `callable/${name}`, op: "http.client" },
      async (span) => {
        const user = getAuth(functions.app).currentUser;
        const headers = { "Content-Type": "application/json" };
        if (user) headers.Authorization = `Bearer ${await user.getIdToken()}`;

        const response = await fetch(callableUrl(functions, name), {
          method: "POST",
          headers,
          body: JSON.stringify({ data: { ...data, ...traceFields(span) } }),
          signal,
        });

        // The callable protocol answers 200 with {result}, and a non-2xx with
        // {error: {status, message, details}}. A body that is neither (an
        // HTML proxy error page, say) still has to become a thrown error
        // carrying the status, or the caller would read `undefined` as "no
        // stragglers" — the exact silent-wrong-answer this call is guarded
        // against.
        const body = await response.json().catch(() => null);
        if (!response.ok || !body || body.error) {
          const error = new Error(
            body?.error?.message ??
              (body
                ? `HTTP ${response.status}`
                : "Callable response was not JSON"),
          );
          error.code = `functions/${body?.error?.status?.toLowerCase() ?? "internal"}`;
          error.details = body?.error?.details ?? null;
          error.httpStatus = response.status;
          throw error;
        }
        return { data: body?.result };
      },
    );
}
